import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Printer, Send, Download, Edit, Trash2, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { EmailStatus } from '../components/EmailStatus';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/apiError';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COMPANY_INFO = {
  name: "NJK Logistics Pte Ltd",
  address: "212A Punggol Walk #12-741 Singapore 821212",
  phone: "98366186",
  email: "inquiry@njklogistics.com",
  paynow_uen: "202606027K"
};

export const SOADetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [soa, setSOA] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    fetchSOA();
  }, [id]);

  const fetchSOA = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/soa/${id}`);
      setSOA(response.data);
    } catch (error) {
      toast.error('Failed to load Statement of Account');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => window.print();

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/soa/${id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `SOA_${soa.document_number}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleSendEmail = async () => {
    if (!soa.customer_email) {
      toast.error('No customer email found');
      return;
    }
    setSendingEmail(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/soa/${id}/send`);
      if (response.data.success) {
        toast.success(`Statement sent to ${soa.customer_email}`);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to send email'));
    } finally {
      setSendingEmail(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete ${soa.document_number}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/soa/${id}`);
      toast.success('Statement deleted');
      navigate('/admin/billing');
    } catch (error) {
      toast.error('Failed to delete statement');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-SG', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const formatCurrency = (amount) => `$${(amount || 0).toFixed(2)}`;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-40 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!soa) {
    return (
      <div className="min-h-screen bg-gray-50 pt-40 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Statement Not Found</h2>
          <Button onClick={() => navigate('/admin/billing')}>Back to Billing</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-40">
      {/* Header - Hidden on Print */}
      <div className="bg-white border-b print:hidden">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-wrap justify-between items-center gap-4">
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={() => navigate('/admin/billing')} data-testid="soa-back-btn">
                <ArrowLeft size={20} />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{soa.document_number}</h1>
                <p className="text-gray-600 mt-1">Statement of Account</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => navigate(`/admin/billing/soa/${id}/edit`)} variant="outline" data-testid="edit-soa-btn">
                <Edit size={18} className="mr-1" /> Edit
              </Button>
              <Button onClick={() => window.open(`${BACKEND_URL}/api/soa/${id}/pdf`, '_blank')} variant="outline" data-testid="preview-pdf-btn">
                <FileText size={18} className="mr-1" /> Preview PDF
              </Button>
              <Button onClick={handleDownloadPdf} variant="outline" disabled={downloadingPdf} data-testid="download-soa-pdf-btn">
                <Download size={18} className="mr-1" /> {downloadingPdf ? 'Downloading...' : 'Download PDF'}
              </Button>
              <Button onClick={handlePrint} variant="outline" data-testid="print-soa-btn">
                <Printer size={18} className="mr-1" /> Print
              </Button>
              <Button onClick={handleSendEmail} className="bg-indigo-600 hover:bg-indigo-700 text-white" disabled={sendingEmail} data-testid="send-soa-email-btn">
                <Send size={18} className="mr-1" /> {sendingEmail ? 'Sending...' : 'Send to Customer'}
              </Button>
              <Button onClick={handleDelete} variant="outline" className="text-red-600 hover:bg-red-50" data-testid="delete-soa-btn">
                <Trash2 size={18} className="mr-1" /> Delete
              </Button>
            </div>
          </div>
          <EmailStatus docType="soa" docId={id} />
        </div>
      </div>

      {/* Document Content */}
      <div className="container mx-auto px-4 py-8 print:px-0 print:py-0">
        <div className="max-w-4xl mx-auto">
          <Card className="print:shadow-none print:border-none">
            <CardContent className="p-8">
              {/* Document Header */}
              <div className="flex justify-between items-start mb-8 border-b pb-6">
                <div>
                  <h2 className="text-2xl font-bold text-indigo-600">{COMPANY_INFO.name}</h2>
                  <p className="text-gray-600 mt-1">{COMPANY_INFO.address}</p>
                  <p className="text-gray-600">Tel: {COMPANY_INFO.phone}</p>
                  <p className="text-gray-600">Email: {COMPANY_INFO.email}</p>
                </div>
                <div className="text-right">
                  <h1 className="text-2xl font-bold text-gray-900">STATEMENT OF ACCOUNT</h1>
                  <p className="text-xl font-semibold text-indigo-600 mt-2">{soa.document_number}</p>
                </div>
              </div>

              {/* Customer & Period Info */}
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Account Holder:</h3>
                  <p className="font-bold text-lg">{soa.customer_name}</p>
                  <p className="text-gray-600">{soa.customer_email}</p>
                  {soa.customer_phone && <p className="text-gray-600">{soa.customer_phone}</p>}
                  {soa.customer_address && <p className="text-gray-600">{soa.customer_address}</p>}
                </div>
                <div className="text-right">
                  <div className="mb-2">
                    <span className="text-gray-600">Statement Date: </span>
                    <span className="font-semibold">{formatDate(soa.created_at)}</span>
                  </div>
                  <div className="mb-2">
                    <span className="text-gray-600">Period: </span>
                    <span className="font-semibold">{formatDate(soa.start_date)} - {formatDate(soa.end_date)}</span>
                  </div>
                </div>
              </div>

              {/* Summary Box */}
              <div className="bg-indigo-50 p-6 rounded-lg mb-8 print:bg-white print:border print:border-indigo-200">
                <div className="grid md:grid-cols-3 gap-6 text-center">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Total Invoiced</p>
                    <p className="text-2xl font-bold text-gray-900" data-testid="soa-total-invoiced">{formatCurrency(soa.total_invoiced)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Total Paid</p>
                    <p className="text-2xl font-bold text-green-600" data-testid="soa-total-paid">{formatCurrency(soa.total_paid)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Balance Due</p>
                    <p className={`text-2xl font-bold ${soa.balance_due > 0 ? 'text-red-600' : 'text-green-600'}`} data-testid="soa-balance-due">
                      {formatCurrency(soa.balance_due)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Transactions Table */}
              <div className="mb-8">
                <h3 className="font-semibold text-gray-700 mb-4">Transaction History</h3>
                {soa.transactions && soa.transactions.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse text-sm" data-testid="soa-transactions-table">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="border p-2 text-left">Date</th>
                          <th className="border p-2 text-left">Document</th>
                          <th className="border p-2 text-left">Description</th>
                          <th className="border p-2 text-right">Debit</th>
                          <th className="border p-2 text-right">Credit</th>
                          <th className="border p-2 text-right">Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {soa.transactions.map((txn, index) => (
                          <tr key={index} className={txn.document_type === 'Payment' ? 'bg-green-50' : ''}>
                            <td className="border p-2">{txn.date}</td>
                            <td className="border p-2 font-mono text-xs">{txn.document_number}</td>
                            <td className="border p-2">{txn.description}</td>
                            <td className="border p-2 text-right">{txn.debit > 0 ? formatCurrency(txn.debit) : '-'}</td>
                            <td className="border p-2 text-right text-green-600">{txn.credit > 0 ? formatCurrency(txn.credit) : '-'}</td>
                            <td className="border p-2 text-right font-semibold">{formatCurrency(txn.balance)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-gray-100 font-bold">
                          <td colSpan="3" className="border p-2 text-right">Totals:</td>
                          <td className="border p-2 text-right">{formatCurrency(soa.total_invoiced)}</td>
                          <td className="border p-2 text-right text-green-600">{formatCurrency(soa.total_paid)}</td>
                          <td className="border p-2 text-right text-indigo-600">{formatCurrency(soa.balance_due)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                    No transactions found for this period.
                  </div>
                )}
              </div>

              {/* Balance Due Notice */}
              {soa.balance_due > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8 print:bg-white">
                  <h4 className="font-semibold text-red-800 mb-2">Amount Due</h4>
                  <p className="text-red-700">
                    Please remit payment of <strong>{formatCurrency(soa.balance_due)}</strong> at your earliest convenience.
                  </p>
                </div>
              )}

              {/* Payment Info with QR */}
              <div className="border-t pt-6 bg-indigo-50 -mx-8 px-8 -mb-8 pb-8 rounded-b-lg print:bg-white">
                <h3 className="font-semibold text-gray-700 mb-3">Payment Information:</h3>
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex-1">
                    <p className="text-gray-600">PayNow UEN: <span className="font-semibold">{COMPANY_INFO.paynow_uen}</span></p>
                    <p className="text-sm text-gray-500 mt-1">Scan the QR code to pay via PayNow</p>
                    <p className="text-sm text-gray-500 mt-2">Please use your company name or invoice number as payment reference.</p>
                  </div>
                  <img src="/qr_code.jpeg" alt="PayNow QR Code" className="w-32 h-32 object-contain border border-gray-200 rounded bg-white p-1" data-testid="paynow-qr" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
